import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Sparkles, Image as ImageIcon, Send, Trash2, Wand2, Clock, CheckCircle2 } from "lucide-react";
import api, { API } from "@/lib/api";

const PLATFORMS = ["instagram", "facebook", "twitter"];
const statusColor = { draft: "bg-slate-200", approved: "bg-sun", scheduled: "bg-sky", posted: "bg-green-200" };

export default function SocialTab() {
  const [posts, setPosts] = useState([]);
  const [topic, setTopic] = useState("");
  const [platform, setPlatform] = useState("instagram");
  const [variants, setVariants] = useState([]);
  const [genLoading, setGenLoading] = useState(false);
  const [imgPrompt, setImgPrompt] = useState("");
  const [imgLoading, setImgLoading] = useState(false);
  const [draft, setDraft] = useState({ caption: "", image_file_id: null, scheduled_at: "" });

  const load = () => api.get("/admin/social-posts").then((r) => setPosts(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const genCaptions = async () => {
    if (!topic.trim()) return toast.error("Enter a topic");
    setGenLoading(true);
    try {
      const r = await api.post("/admin/ai/social-captions", { topic, platform, count: 3 });
      setVariants(r.data.variants || []);
    } catch { toast.error("Generation failed"); } finally { setGenLoading(false); }
  };

  const genImage = async () => {
    if (!imgPrompt.trim()) return toast.error("Describe the image");
    setImgLoading(true);
    try {
      const r = await api.post("/admin/ai/image", { prompt: imgPrompt });
      setDraft({ ...draft, image_file_id: r.data.file_id });
      toast.success("Image generated!");
    } catch { toast.error("Image generation failed"); } finally { setImgLoading(false); }
  };

  const savePost = async (status) => {
    if (!draft.caption.trim()) return toast.error("Caption is empty");
    const body = { platform, caption: draft.caption, image_file_id: draft.image_file_id, status };
    if (status === "scheduled") {
      if (!draft.scheduled_at) return toast.error("Pick a date/time to schedule");
      body.scheduled_at = new Date(draft.scheduled_at).toISOString();
    }
    try {
      await api.post("/admin/social-posts", body);
      toast.success(status === "scheduled" ? "Post scheduled" : "Saved to queue");
      setDraft({ caption: "", image_file_id: null, scheduled_at: "" });
      setVariants([]); setTopic(""); setImgPrompt(""); load();
    } catch { toast.error("Failed"); }
  };

  const updateStatus = async (id, status, scheduled_at) => {
    try { await api.put(`/admin/social-posts/${id}`, { status, ...(scheduled_at ? { scheduled_at } : {}) }); load(); }
    catch { toast.error("Failed"); }
  };
  const del = async (id) => { await api.delete(`/admin/social-posts/${id}`); load(); };

  return (
    <div data-testid="admin-social">
      <h1 className="font-display font-bold text-3xl mb-1">Social Content</h1>
      <p className="text-ink/60 mb-6">Generate posts with AI, review them, and schedule to the queue.</p>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Generator + composer */}
        <div className="brut-card p-5 space-y-4">
          <div className="flex items-center gap-2 font-display font-bold text-xl"><Wand2 className="w-5 h-5 text-bubble" /> AI Post Studio</div>
          <div className="flex flex-wrap gap-2">
            <select value={platform} onChange={(e) => setPlatform(e.target.value)} className="brut-input !py-2 !w-auto text-sm" data-testid="social-platform">
              {PLATFORMS.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
            <input className="brut-input !py-2 flex-1 min-w-[180px] text-sm" placeholder="Topic e.g. 'holiday gift caricatures'"
              value={topic} onChange={(e) => setTopic(e.target.value)} data-testid="social-topic" />
            <button onClick={genCaptions} disabled={genLoading} className="brut-btn !py-2 !px-4 bg-sun text-sm" data-testid="gen-captions">
              <Sparkles className="w-4 h-4" /> {genLoading ? "Writing…" : "Generate"}
            </button>
          </div>

          {variants.length > 0 && (
            <div className="space-y-2" data-testid="caption-variants">
              {variants.map((v, i) => (
                <button key={i} onClick={() => setDraft({ ...draft, caption: v })}
                  className="w-full text-left brut border-ink bg-cream-solid p-3 text-sm hover:bg-sun/30 transition-colors" data-testid={`caption-variant-${i}`}>
                  {v}
                </button>
              ))}
            </div>
          )}

          <div className="border-t-2 border-dashed border-ink/20 pt-4 space-y-2">
            <div className="flex gap-2">
              <input className="brut-input !py-2 flex-1 text-sm" placeholder="Describe an image to generate…"
                value={imgPrompt} onChange={(e) => setImgPrompt(e.target.value)} data-testid="image-prompt" />
              <button onClick={genImage} disabled={imgLoading} className="brut-btn !py-2 !px-4 bg-grape text-white text-sm" data-testid="gen-image">
                <ImageIcon className="w-4 h-4" /> {imgLoading ? "Drawing…" : "Image"}
              </button>
            </div>
            {draft.image_file_id && (
              <img src={`${API}/files/${draft.image_file_id}`} alt="generated" className="w-full h-48 object-cover brut border-ink" data-testid="draft-image" />
            )}
          </div>

          <textarea className="brut-input min-h-[100px]" placeholder="Your caption…" value={draft.caption}
            onChange={(e) => setDraft({ ...draft, caption: e.target.value })} data-testid="draft-caption" />
          <input type="datetime-local" className="brut-input !py-2 text-sm" value={draft.scheduled_at}
            onChange={(e) => setDraft({ ...draft, scheduled_at: e.target.value })} data-testid="draft-schedule" />
          <div className="flex flex-wrap gap-2">
            <button onClick={() => savePost("draft")} className="brut-btn !py-2 !px-4 bg-white text-sm" data-testid="save-draft">Save to queue</button>
            <button onClick={() => savePost("scheduled")} className="brut-btn !py-2 !px-4 bg-bubble text-white text-sm" data-testid="schedule-post"><Clock className="w-4 h-4" /> Schedule</button>
          </div>
        </div>

        {/* Queue */}
        <div className="space-y-3">
          <div className="font-display font-bold text-xl">Content Queue ({posts.length})</div>
          {posts.length === 0 && <div className="brut-card p-6 text-ink/60">Nothing here yet — generate a post!</div>}
          {posts.map((p) => (
            <div key={p.id} className="brut-card p-4" data-testid={`social-post-${p.id.slice(0, 8)}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="tag-pill bg-white capitalize">{p.platform}</span>
                  <span className={`tag-pill ${statusColor[p.status] || "bg-white"} capitalize`}>{p.status}</span>
                </div>
                <button onClick={() => del(p.id)} className="text-tangerine"><Trash2 className="w-4 h-4" /></button>
              </div>
              {p.image_file_id && <img src={`${API}/files/${p.image_file_id}`} alt="" className="w-full h-40 object-cover brut border-ink mb-2" />}
              <p className="text-sm whitespace-pre-line">{p.caption}</p>
              {p.scheduled_at && <div className="text-xs text-ink/50 mt-2"><Clock className="w-3 h-3 inline" /> {new Date(p.scheduled_at).toLocaleString()}</div>}
              {p.status !== "posted" && (
                <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t-2 border-dashed border-ink/20">
                  {p.status === "draft" && <button onClick={() => updateStatus(p.id, "approved")} className="brut-btn !py-1.5 !px-3 bg-sun text-xs" data-testid={`approve-${p.id.slice(0, 8)}`}><CheckCircle2 className="w-3.5 h-3.5" /> Approve</button>}
                  {p.scheduled_at && p.status !== "scheduled" && <button onClick={() => updateStatus(p.id, "scheduled")} className="brut-btn !py-1.5 !px-3 bg-sky text-xs">Schedule</button>}
                  <button onClick={() => updateStatus(p.id, "posted")} className="brut-btn !py-1.5 !px-3 bg-green-200 text-xs" data-testid={`mark-posted-${p.id.slice(0, 8)}`}><Send className="w-3.5 h-3.5" /> Mark posted</button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
